export default function Home() {
  return (
    <>
      <Hero />
      <About />
      <Capabilities />
      <Approach />
      <Contact />
    </>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div
        className="hero-bg"
        aria-hidden="true"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/poc-hero.jpg)` }}
      />
      <div className="hero-bg-label">[ AERIAL · RAIL · OVERWATCH ]</div>

      <div className="wrap hero-content">
        <div className="eyebrow reveal" style={{ marginBottom: 28 }}>
          RFS · 001 / Overwatch &amp; Inspection
        </div>
        {/* Headline sits above the grid so it runs the full container width --
            inside the text column it wrapped mid-phrase at large viewports. */}
        <h1 className="display h1 hero-title reveal d1">
          Aerial intelligence <br />for <em>rail.</em>
        </h1>

        <div className="hero-grid">
          <p className="lede reveal d2">
            Railway Flight Solutions (RFS) is transforming railway operations through the innovative integration of autonomous unmanned aircraft systems (UAS), fixed camera networks, and AI-driven machine vision. Our intelligent monitoring solutions provide persistent infrastructure inspection, security overwatch, and real-time operational awareness that extend beyond the capabilities of traditional inspection and monitoring methods. From Maintenance-of-Way (MOW) and mechanical inspections to asset security, continuous asset monitoring, and rapid incident response, RFS delivers actionable intelligence that enables railroads to operate with improved safety, efficiency, and confidence—reducing delays, improving resilience, and protecting both people and critical infrastructure.
          </p>

          <aside className="hero-meta reveal d3" aria-label="At a glance">
            <div className="hero-meta-row"><span className="k">Sector</span><span className="v">Rail · Critical Infrastructure</span></div>
            <div className="hero-meta-row"><span className="k">Method</span><span className="v">UAS · Fixed Camera · ML Vision</span></div>
            <div className="hero-meta-row"><span className="k">Mission</span><span className="v">Inspection &amp; Overwatch</span></div>
            <div className="hero-meta-row"><span className="k">Based</span><span className="v">Fort Worth, Texas</span></div>
          </aside>
        </div>
      </div>

      <div className="scroll-cue" aria-hidden="true">
        <span>Scroll</span>
        <span className="scroll-cue-line" />
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="about" id="about">
      <div className="wrap">
        <div className="section-hd reveal">
          <div className="num">[ 02 ] &nbsp; About</div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 22 }}>Why Us · Advantages</div>
            <h2 className="display h2">
              Proprietary machine vision,<br />
              engineered for <em>real-world</em> rail.
            </h2>
          </div>
        </div>

        <div className="about-grid">
          <div className="about-meta reveal">
            <div className="about-stat"><div className="n">01</div><div className="l">Proprietary ML Stack</div></div>
            <div className="about-stat"><div className="n">02</div><div className="l">Unique Data Collection</div></div>
            <div className="about-stat"><div className="n">03</div><div className="l">Field-Hardened Operations</div></div>
          </div>
          <div className="reveal d1">
            <p className="body-lg">
              Railway Flight Solutions leverages proprietary machine vision technology with unique data collection techniques to create innovative solutions that improve decision-making in real-world railroad operations.
            </p>
          </div>
          <div className="reveal d2">
            <p className="body">
              Our commitment to excellence ensures accurate data, improved efficiency, and heightened safety and security for our clients — across inspection, monitoring, and overwatch missions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Capabilities() {
  const items = [
    {
      n: '01',
      word: 'Precision.',
      tag: 'High-Precision Imaging',
      hd: <><em>Accurate</em> data, by design.</>,
      body:
        'We employ advanced high-precision camera systems to enable robust data collection — supporting detailed inspections for comprehensive security assessments and optimized maintenance planning.'
    },
    {
      n: '02',
      word: 'Efficiency.',
      tag: 'Real-Time Diagnostics',
      hd: <><em>Streamlined</em> rail operations.</>,
      body:
        'Optimize railway operations with our advanced technology. Our solutions enhance physical-plant monitoring and issue detection — streamlining workflows and minimizing downtime through real-time data analysis and automated diagnostics.'
    },
    {
      n: '03',
      word: 'Safety.',
      tag: 'Predictive Analytics',
      hd: <><em>Enhanced</em> safety and security.</>,
      body:
        'Safety is paramount. Our cutting-edge technology bolsters railway safety and security by enabling proactive risk detection and rapid response to potential issues — through real-time monitoring and predictive analytics.'
    }
  ];

  return (
    <section className="caps" id="capabilities">
      <div className="wrap">
        <div className="section-hd reveal">
          <div className="num">[ 03 ] &nbsp; Capabilities</div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 22 }}>Three Pillars</div>
            <h2 className="display h2">
              Precision. Efficiency.<br />
              <em>Safety.</em>
            </h2>
          </div>
        </div>

        <div className="cap-list">
          {items.map((it) => (
            <article className="cap-item reveal" key={it.n}>
              <div className="cap-num">{it.n}</div>
              <div className="cap-name">
                <div className="word">{it.word}</div>
                <span className="tag">{it.tag}</span>
              </div>
              <div className="cap-title">
                <h3 className="hd">{it.hd}</h3>
                <p className="cap-body">{it.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section className="plate" id="approach">
      <div className="plate-bg" aria-hidden="true" />
      <div className="plate-label">[ A · 04 ] &nbsp; Approach</div>
      <div className="wrap plate-content">
        <p className="plate-quote reveal">
          Eyes in the air.<br />Intelligence on the <em>ground.</em>
        </p>
        <div className="plate-cite reveal d1">
          <span className="l1">Operating Doctrine</span>
          <span className="l2">Inspection · Overwatch · Response</span>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="contact" id="contact">
      <div className="wrap">
        <div className="contact-grid">
          <div className="contact-card reveal">
            <div className="contact-card-head">
              <div className="num">[ 05 ] &nbsp; Contact</div>
              <div className="eyebrow" style={{ marginBottom: 22 }}>Please write us</div>
              <h2 className="display h2 contact-title">
                Let's discuss your<br />
                <em>mission profile.</em>
              </h2>
            </div>
            <div className="contact-row">
              <div className="k">Email</div>
              <div className="v">
                <a href="mailto:info@railwayflightsolutions.com">info@railwayflightsolutions.com</a>
              </div>
            </div>
            <div className="contact-row">
              <div className="k">Location</div>
              <div className="v">
                Fort Worth, TX
                <br />
                <span style={{ color: 'var(--ink-3)', fontSize: 14 }}>United States</span>
              </div>
            </div>
            <div className="contact-row">
              <div className="k">Inquiries</div>
              <div className="v" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                Inspection contracts · Overwatch missions · Pilot programs
              </div>
            </div>
          </div>

          <div
            className="reveal d1"
            style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 8 }}
          >
            <div className="mono">Operating Region</div>
            <div
              style={{
                position: 'relative',
                aspectRatio: '530 / 505',
                border: '1px solid var(--line)',
                overflow: 'hidden',
                background: 'var(--bg-2)'
              }}
            >
              <img
                src="/assets/rail-network-map.png"
                alt="Map of Class I railroad networks across the continental United States"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <div className="mono" style={{ position: 'absolute', top: 12, left: 12 }}>N</div>
              <div className="mono" style={{ position: 'absolute', bottom: 12, right: 12 }}>
                [ CLASS I NETWORK ]
              </div>
            </div>
            <div style={{ color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.5 }}>
              Headquartered in Fort Worth, TX — capable of deployment across railroads in North America and worldwide.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
